import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import type { ApiClient } from 'src/store/backend';
import { getClassificationText } from 'src/store/selectors';
import { currentUserUuid } from '../../store/selectors/user/currentUserUuid';
import { writeAccess } from '../../store/selectors/user/writeAccess';
import { getOrganization } from '../../store/selectors/organization/getOrganization';
import type { CompetenceViewEditItem, CompetenceType } from '../../content/competence/competence-view-edit-item';
import type { CompetenceStatus, CompetenceView } from '../../content/competence/competence-view';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { SelectTagOption } from '../../library/editors/elements/select-tag-option';
import { assertIsDefined } from '../../assertIsDefined';
import { toAttachmentItems } from '../to-attachment-items';
import { t } from 'src/i18n';

// Category values are Norwegian HMS/legal-role domain terms (verneombud, AMU,
// daglig leder …) stored verbatim in the backend `category` field. They are
// treated as data, not UI chrome, so they are intentionally not translated.
const COMPETENCE_CATEGORIES: string[] = [
  'HMS daglig leder', // i18n-exempt: HMS-domenebegrep (norsk lovrolle), lagres som data
  'HMS verneombud', // i18n-exempt: HMS-domenebegrep (norsk lovrolle), lagres som data
  'HMS AMU', // i18n-exempt: HMS-domenebegrep (norsk lovrolle), lagres som data
  'Førstehjelp', // i18n-exempt: HMS-domenebegrep, lagres som data
  'Brannvern', // i18n-exempt: HMS-domenebegrep, lagres som data
  'Truck/maskin', // i18n-exempt: HMS-domenebegrep, lagres som data
  'Annet', // i18n-exempt: HMS-domenebegrep, lagres som data
];

function statusText(status: CompetenceStatus): string {
  switch (status) {
    case 'COMPLETED':
      return t('competence.status.completed');
    case 'VALID':
      return t('competence.status.valid');
    case 'EXPIRING_SOON':
      return t('competence.status.expiringSoon');
    case 'EXPIRED':
      return t('competence.status.expired');
    default:
      return '';
  }
}

export async function buildCompetenceView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<CompetenceView> {
  const organization = getOrganization(state);
  assertIsDefined(organization, 'organization for buildCompetenceView');

  const entity = (organization.competenceById ?? {})[uuid];
  assertIsDefined(entity, `competence ${uuid} in organization.competenceById`);

  const competenceType = (entity.type as CompetenceType) ?? 'COMPLETED_TRAINING';
  const status = (entity.status ?? '') as CompetenceStatus;
  const classification = (entity.classification ?? 'NONE') as 'NONE' | 'CONFIDENTIAL';
  const accessControl = entity.accessControl ?? [];

  const categoryOptions: SelectTagOption[] = COMPETENCE_CATEGORIES.map((c) => ({ value: c, text: c }));

  return {
    ...commonData,
    docsForLinking: [],
    currentUserHasAccess: classification === 'NONE' || accessControl.includes(currentUserUuid(state) ?? ''),
    currentUserHasWriteAccess: writeAccess(state),

    uuid: uuid,
    type: 'competence',
    competenceType: competenceType,
    employeeUuid: entity.employeeUuid ?? '',
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: '',
    href: currentParent + '/competence/' + uuid,
    pdfHref: '',
    parentHref: currentParent,
    deleted: entity.deleted === true,
    deletable: true,
    name: entity.name ?? '',
    category: entity.category ?? '',
    description: entity.description ?? '',
    completedDate: entity.completedDate ?? '',
    provider: entity.provider ?? '',
    hours: entity.hours,
    expiryDate: entity.expiryDate ?? '',
    notifyDaysBefore: entity.notifyDaysBefore,
    notificationEnabled: entity.notificationEnabled ?? false,
    certificateNumber: entity.certificateNumber ?? '',
    status: status,
    statusText: statusText(status),
    classification: classification,
    accessControl: accessControl,
    classificationText: getClassificationText(classification, accessControl, commonData.accessControlOptions),
    categoryOptions: categoryOptions,
    attachments: toAttachmentItems(api, 'competence', entity.uuid, entity.attachments),
    today: LocalDate.fromString(state.today),
    hasDraft: false,
    fetchDraft: async (): Promise<CompetenceViewEditItem> => {
      return {
        employeeUuid: entity.employeeUuid ?? '',
        type: competenceType,
        category: entity.category ?? '',
        name: entity.name ?? '',
        description: entity.description ?? '',
        completedDate: entity.completedDate ?? '',
        provider: entity.provider ?? '',
        hours: entity.hours === undefined ? '' : '' + entity.hours,
        expiryDate: entity.expiryDate ?? '',
        notifyDaysBefore: entity.notifyDaysBefore === undefined ? '' : '' + entity.notifyDaysBefore,
        notificationEnabled: entity.notificationEnabled ?? false,
        certificateNumber: entity.certificateNumber ?? '',
        classification: classification,
        accessControl: accessControl,
      };
    },
  };
}
